import React, { useRef, useState } from 'react';
import { exporter, importer, lireFichier } from '../stockage/sauvegarde.js';

/**
 * Section Sauvegarde de Réglages - document 04, écran 3.
 *
 * Filet de sécurité indépendant du serveur : il permet aussi de basculer
 * d'appareil sans réseau, ce que la synchronisation ne sait pas faire.
 */
export default function Sauvegarde({ onDonneesChangees }) {
  const [etat, setEtat] = useState(null);
  const [mode, setMode] = useState('fusionner');
  const champFichier = useRef(null);

  const lancerExport = async () => {
    try {
      const { profils, devis, mouvements } = await exporter();
      setEtat({ ok: true, message: `Exporté - ${profils} profil(s), ${devis} devis, ${mouvements} mouvement(s).` });
    } catch (e) {
      setEtat({ ok: false, message: e.message });
    }
  };

  const lancerImport = async (fichier) => {
    if (!fichier) return;
    // Remplacer est destructif : on demande confirmation, en disant ce qui va
    // disparaître plutôt qu'en posant une question vague.
    if (mode === 'remplacer' &&
        !window.confirm('Remplacer effacera tous les profils et devis de cet appareil avant de charger le fichier. Continuer ?')) {
      return;
    }
    try {
      const doc = await lireFichier(fichier);
      const { ecrits, ignores } = await importer(doc, mode);
      await onDonneesChangees();
      setEtat({
        ok: true,
        message: `Importé - ${ecrits} entrée(s) écrite(s)` +
          (ignores ? `, ${ignores} ignorée(s) car la version locale est plus récente.` : '.'),
      });
    } catch (e) {
      setEtat({ ok: false, message: e.message });
    } finally {
      // Sans cela, réimporter le même fichier ne déclencherait aucun événement.
      if (champFichier.current) champFichier.current.value = '';
    }
  };

  return (
    <section>
      <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)' }}>
        Sauvegarde
      </h2>

      <p style={{ margin: '0 0 var(--espace-3)', fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
        Filet de sécurité indépendant du serveur : profils, devis et mouvements.
        Les réglages de cet appareil - URL, jeton, apparence - ne sont jamais
        exportés. Après un import, le livre est reconstruit depuis les
        mouvements.
      </p>

      <div style={{ display: 'grid', gap: 'var(--espace-3)', maxWidth: 520 }}>
        <div>
          <button
            type="button"
            onClick={lancerExport}
            style={{
              padding: 'var(--espace-2) var(--espace-4)', border: '1px solid var(--couleur-accent)',
              borderRadius: 'var(--rayon)', background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
            }}
          >
            Exporter
          </button>
        </div>

        <fieldset style={{ border: '1px solid var(--couleur-trait-fort)', borderRadius: 'var(--rayon)', padding: 'var(--espace-3)' }}>
          <legend className="etiquette" style={{ padding: '0 var(--espace-1)' }}>Importer</legend>

          <div role="radiogroup" aria-label="Mode d’import" style={{ display: 'grid', gap: 'var(--espace-1)', marginBottom: 'var(--espace-3)' }}>
            {[
              ['fusionner', 'Fusionner', 'Une entrée du fichier ne remplace la locale que si elle est plus récente.'],
              ['remplacer', 'Remplacer', 'Efface tout sur cet appareil, puis charge le fichier.'],
            ].map(([cle, libelle, aide]) => (
              <label key={cle} style={{ display: 'flex', gap: 'var(--espace-2)', alignItems: 'flex-start' }}>
                <input
                  type="radio" name="mode-import" value={cle}
                  checked={mode === cle} onChange={() => setMode(cle)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong style={{ fontSize: '0.9rem' }}>{libelle}</strong>
                  <span style={{ display: 'block', fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>{aide}</span>
                </span>
              </label>
            ))}
          </div>

          {/* Étiquette mise en forme plutôt que le sélecteur natif : celui-ci
              fait 22 px de haut, sous le seuil de cible tactile, et ne se met
              pas en forme. Le champ reste dans le DOM et reste focalisable -
              le masquer par `display: none` le retirerait de la navigation au
              clavier. */}
          <label
            className="etiquette-fichier"
            style={{
              display: 'inline-flex', alignItems: 'center', minHeight: 32,
              padding: 'var(--espace-2) var(--espace-4)',
              border: '1px solid var(--couleur-trait-fort)',
              borderRadius: 'var(--rayon)',
              background: 'transparent', color: 'var(--couleur-encre)',
              fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            Choisir un fichier…
            <input
              ref={champFichier}
              type="file"
              accept="application/json,.json"
              onChange={(e) => lancerImport(e.target.files?.[0])}
              aria-label="Fichier à importer"
              className="hors-ecran"
            />
          </label>
        </fieldset>

        <div aria-live="polite" style={{ minHeight: '1.25rem' }}>
          {etat && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: etat.ok ? 'var(--couleur-succes)' : 'var(--couleur-alerte)' }}>
              {etat.message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
